// 담당: 현정
// Creators — Profile 섹션.
//   브랜드 제안서의 원천 데이터 레이어. 6블록:
//   ① 상단 4타일(소속·파트너십LMPA·주력카테고리·활동채널) + ② 메타 스트립(계약·국가·언어·매니저)
//   ③ 뷰티 속성  ④ 채널 규모  ⑤ 대표 콘텐츠  ⑥ 활동 이력 | 협업 브랜드
//
// 데이터 연결은 OverviewSection과 동일: 실 API(fetchProfile)만 호출. mock 폴백 없음.
//   · 200 → 실데이터  · 401 + dev → Bearer 토큰 입력창  · 그 외/연결실패 → 에러 화면 + 재시도.
//   · reqIdRef 로 크리에이터 스위칭/재조회 race 가드(최신 요청만 반영).
//   · null/빈 데이터는 각 블록에서 "—" 또는 섹션 생략(graceful degrade) — 가짜 데이터 없음.
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchAudience,
  fetchProfile,
  setLecommendToken,
  type AudienceDemographics,
  type Platform,
  type ProfileData,
  type ProfileSlice,
} from '../creatorsApi'
import { usePlatformData } from './usePlatformData'
import { AudienceDemographicsPanel } from './profile/AudienceDemographics'
import { IdentityTiles } from './profile/IdentityTiles'
import { MetaStrip } from './profile/MetaStrip'
import { BeautyProfile } from './profile/BeautyProfile'
import { ChannelScale } from './profile/ChannelScale'
import { RepresentativeContent } from './profile/RepresentativeContent'
import { ALL_PLATFORMS } from './platformDefault'
import { mergeHistoryAcrossChannels } from './profile/historyRows'
import { NotableHistory } from './profile/NotableHistory'
import { CollaborationBrands } from './profile/CollaborationBrands'

interface Props {
  creatorId: string
  /** 셸이 URL 로 쥔 플랫폼. 탭은 여전히 블록이 소유한다 — 정체성·채널 규모는 3채널을
   *  함께 보여주는 블록이라 그쪽엔 탭이 붙지 않는다. */
  platform: Platform
  /** Profile 응답에는 플랫폼별 표본 신호가 없어 여기선 보정하지 않는다. 계약만 맞춘다. */
  platformPinned: boolean
  onPlatformChange: (platform: Platform) => void
}

/** 구버전 BE 폴백 — byPlatform 이 없으면 최상위 필드가 곧 그 채널(요청 채널)의 값이다. */
function fallbackSlice(data: ProfileData | null): ProfileSlice | undefined {
  if (!data) return undefined
  return {
    representativeContent: data.representativeContent,
    collaborationBrands: data.collaborationBrands,
    collaborationBrandsTotal: data.collaborationBrands.length,
    history: data.history,
    meta: data.meta,
  }
}

export function ProfileSection({ creatorId, platform: initialPlatform }: Props) {
  // 탭은 **블록마다 독립**이다 — 대표 콘텐츠에서 TikTok 을 눌러도 협업 브랜드는 그대로 남는다.
  // 셸이 준 값은 두 블록의 **시작값**일 뿐, 이후엔 각자 움직인다.
  const [repPlatform, setRepPlatform] = useState<Platform>(initialPlatform)
  const [brandPlatform, setBrandPlatform] = useState<Platform>(initialPlatform)
  const [tokenInput, setTokenInput] = useState('')
  // 오디언스 인구통계 — 부가 블록(포털 Mongo, OAuth 연동자만). 본문(fetchProfile)과 독립 병렬 조회,
  // 실패/null 은 조용히 섹션 생략. 본문 에러 화면에는 영향 0.
  const [audience, setAudience] = useState<AudienceDemographics | null>(null)
  const reqIdRef = useRef(0) // 크리에이터 스위칭/재조회 race 가드 — 최신 요청만 반영

  // 한 번 조회로 세 채널분이 온다 — 탭은 그중 무엇을 보여줄지만 고른다(전환 시 조회 없음).
  const profiles = usePlatformData(creatorId, fetchProfile, (d) => d.byPlatform)
  const data = profiles.data
  const error = profiles.error
  // 구버전 BE(byPlatform 미도달)면 최상위 필드로 폴백한다 — 배포 순서와 무관하게 화면이 안 빈다.
  // 빈 사유 판정 입력 — Profile 응답의 채널 목록을 오디언스 스트립과 같은 모양으로 맞춘다.
  const profileChannels = (data?.channelScale.channels ?? []).map((c) => ({
    platform: c.platform,
    handle: c.name ?? '',
    subscribers: c.subscribers,
    linked: c.lastMediaCollectedAt != null,
    main: false,
    autoCollect: c.lastMediaCollectedAt != null,
  }))
  const repData = profiles.slice(repPlatform) ?? fallbackSlice(data)
  const brandData = profiles.slice(brandPlatform) ?? fallbackSlice(data)
  // 활동 이력만 탭을 안 탄다 — 전 채널 조각을 합쳐 한 벌로 그린다(구버전 BE 는 최상위가 유일한 조각).
  const mergedHistory = useMemo(
    () =>
      mergeHistoryAcrossChannels([
        ...ALL_PLATFORMS.map((platform) => ({
          platform,
          history: data?.byPlatform?.[platform]?.history,
        })),
        // 구버전 BE(byPlatform 미도달) 폴백 — 최상위가 유일한 조각이다(= 요청 채널 = YouTube).
        { platform: undefined, history: data?.byPlatform ? undefined : data?.history },
      ]),
    [data],
  )

  useEffect(() => {
    const reqId = ++reqIdRef.current
    setAudience(null)
    fetchAudience(creatorId).then((a) => {
      if (reqId === reqIdRef.current) setAudience(a)
    })
  }, [creatorId])

  function submitToken() {
    if (!tokenInput.trim()) return
    setLecommendToken(tokenInput.trim())
    setTokenInput('')
    profiles.reload()
  }

  if (error) {
    const isAuth = error.status === 401
    return (
      <div className="border border-line-hairline bg-surface-base p-8">
        <div className="font-display text-[16px] font-bold text-fg-1">
          {isAuth ? '로그인이 필요합니다' : '데이터를 불러오지 못했습니다'}
        </div>
        <div className="mt-2 font-text text-[14px] text-fg-3">
          {isAuth
            ? 'Leferi Apps 로그인이 필요합니다.'
            : `${error.message}${error.transport && error.status ? ` (${error.status})` : ''}`}
        </div>

        {/* 로컬 dev 실연결 테스트 — Bearer 토큰 붙여넣기 (Overview와 동일) */}
        {isAuth && import.meta.env.DEV && (
          <div className="mt-4 max-w-md">
            <div className="mb-2 font-text text-[12px] text-fg-3">
              로컬 dev — Bearer 토큰으로 실연결 테스트
            </div>
            <div className="flex gap-2">
              <input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Bearer 토큰 붙여넣기"
                className="flex-1 rounded-r-1 border border-line-soft bg-surface-base px-2 py-1 text-fg-1 focus:shadow-focus focus:outline-none"
              />
              <button
                type="button"
                onClick={submitToken}
                className="rounded-r-1 bg-brand-blue px-3 py-1 font-medium text-fg-inverse hover:opacity-90 focus:shadow-focus focus:outline-none"
              >
                저장 후 재조회
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={profiles.reload}
          className="mt-4 rounded-r-1 border border-line-soft px-3 py-1 font-text text-[14px] text-fg-1 hover:bg-surface-soft focus:shadow-focus focus:outline-none"
        >
          다시 시도
        </button>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="border border-line-hairline bg-surface-base p-8 font-text text-[14px] text-fg-3">
        {profiles.loading ? '불러오는 중…' : '표시할 데이터가 없습니다.'}
      </div>
    )
  }

  return (
    <div>
      {/* ① 정체성 헤더 (4타일 + ② 메타 스트립) */}
      <IdentityTiles
        isLeferi={data.isLeferi}
        label={data.label}
        lmpaParticipant={data.lmpaParticipant}
        categories={data.categories}
        activeChannels={data.activeChannels}
      />
      <MetaStrip
        contractType={data.contractType}
        country={data.country}
        language={data.language}
        manager={data.manager}
      />

      {/* ③ 뷰티 속성 */}
      <BeautyProfile attributes={data.beautyAttributes} />

      {/* ④ 채널 규모 */}
      <ChannelScale scale={data.channelScale} />

      {/* 시청자 분포 — OAuth 연동 크리에이터만(데이터 없으면 컴포넌트가 스스로 생략) */}
      <AudienceDemographicsPanel data={audience} />

      {/* ⑤ 대표 콘텐츠 — 자기 탭·자기 데이터. 아래 협업 브랜드와 서로 간섭하지 않는다. */}
      {/* meta 는 **그 블록이 보고 있는 채널의 것**을 쓴다. 최상위 meta 는 bi_base_data
          (YouTube 전용 레거시) 기준이라, IG·TikTok 탭에서 쓰면 화면이 남의 채널 동기화 시각을
          자기 것처럼 단언한다(실측: IG 데이터가 최신인데 "수집 2025.10.14"). */}
      <RepresentativeContent
        items={repData?.representativeContent ?? []}
        meta={repData?.meta ?? data.meta}
        platform={repPlatform}
        onPlatformChange={setRepPlatform}
        // 미리 받아두므로 보통은 즉시 바뀐다. 아직 안 온 채널을 처음 누른 순간에만 잠깐 뜬다.
        // 한 번에 다 받아두므로 탭 전환엔 대기가 없다. 첫 로딩 때만 뜬다.
        busy={profiles.loading}
        channels={profileChannels}
      />

      {/* ⑥ 활동 이력 | 협업 브랜드 (2열) */}
      <div className="mt-9 grid grid-cols-1 border border-line-hairline bg-paper md:grid-cols-2">
        {/* 활동 이력은 **전 채널 합본**이다 — 크리에이터 단위 커리어라 옆 블록의 협업 브랜드 탭을
            눌렀다고 바뀌면 안 된다. 병합·중복 제거 규칙은 historyRows.ts 가 단일 원천이고,
            정렬은 그 안에서 이미 연도 내림차순(최신순)이다. */}
        <NotableHistory history={mergedHistory} meta={data.meta} />
        <CollaborationBrands
          brands={brandData?.collaborationBrands ?? []}
          platform={brandPlatform}
          onPlatformChange={setBrandPlatform}
          busy={profiles.loading}
          channels={profileChannels}
        />
      </div>
    </div>
  )
}
